import { dungeonService } from './DungeonService';
import { PlacedLocation } from '../types/map.types';
import { LocationDelta } from '../net/sessionEvents';
import { LocationEffectContext } from '../types/location.types';
import { LOCATION_REGISTRY } from '../data/locations.config';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { PlayerEntity } from '../entities/PlayerEntity';
import { TalentEffects } from './TalentEffectsService';
import { useInventoryStore } from '../store/inventorySlice';
import { netService } from './NetService';
import { makeLocationFire } from '../net/sessionEvents';
import {
  isOwnerLocalLocationHandler,
  isConsumptionLocationHandler,
} from '../data/locationEffects';

export class LocationService {
  public dungeonService!: typeof dungeonService;
  private placedLocations: PlacedLocation[] = [];
  private usedLocations = new Set<string>();
  private usedByPlayer = new Map<string, Set<string>>();
  private locationsBreaking = new Set<string>();
  private locationsDestroyed = new Set<string>();

  private locationKey(location: PlacedLocation): string {
    return `${location.id}:${location.x}:${location.y}`;
  }

  /**
   * `PlacedLocation` convention: `(loc.x, loc.y)` is the bottom-left cell
   * (minimum x, maximum y in array convention). The footprint thus spans
   * columns [loc.x, loc.x + width - 1] and rows [loc.y - height + 1, loc.y].
   */
  private coversTile(loc: PlacedLocation, x: number, y: number): boolean {
    return (
      x >= loc.x &&
      x < loc.x + loc.width &&
      y <= loc.y &&
      y > loc.y - loc.height
    );
  }

  public setPlacedLocations(locations: PlacedLocation[]): void {
    this.placedLocations = locations;
  }

  public addPlacedLocation(location: PlacedLocation): void {
    this.placedLocations = [...this.placedLocations, location];
    this.dungeonService.markNeedsRender();
  }

  public removePlacedLocation(id: string): void {
    this.placedLocations = this.placedLocations.filter((l) => l.id !== id);
    this.dungeonService.markNeedsRender();
  }

  public applyLocationDelta(delta: LocationDelta): void {
    if (delta.op === 'add') {
      this.addPlacedLocation(delta.location);
    } else if (delta.op === 'remove') {
      this.removePlacedLocation(delta.id);
    } else {
      const loc = this.placedLocations.find((l) => l.id === delta.id);
      if (!loc) {
        return;
      }
      if (delta.used) {
        this.markLocationUsed(loc);
      }
      if (delta.destroyed) {
        this.triggerLocationBreak(loc);
      }
    }
  }

  public restoreState(usedKeys: string[], destroyedKeys: string[]): void {
    this.usedLocations = new Set(usedKeys);
    this.locationsDestroyed = new Set(destroyedKeys);
  }

  public getUsedLocationKeys(): string[] {
    return Array.from(this.usedLocations);
  }

  public getUsedByPlayer(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const [key, set] of this.usedByPlayer) {
      out[key] = Array.from(set);
    }
    return out;
  }

  public restoreUsedByPlayer(data: Record<string, string[]>): void {
    this.usedByPlayer = new Map(
      Object.entries(data).map(([key, ids]) => [key, new Set(ids)]),
    );
  }

  public getDestroyedLocationKeys(): string[] {
    return Array.from(this.locationsDestroyed);
  }

  /** Destroyed keys for GLOBAL (once-per-spawn) locations only — the safe subset
   *  to replicate to a (re)joining client. A once-per-player break is per-browser
   *  (the breaker's own visual); blanking it on a peer who hasn't consumed it
   *  would show a broken-but-still-lootable location. */
  public getGlobalDestroyedKeys(): string[] {
    return this.placedLocations
      .filter(
        (l) =>
          this.locationsDestroyed.has(this.locationKey(l)) &&
          !LOCATION_REGISTRY[l.id]?.oncePerPlayer,
      )
      .map((l) => this.locationKey(l));
  }

  public getPlacedLocations(): PlacedLocation[] {
    return this.placedLocations;
  }

  public isLocationUsed(location: PlacedLocation): boolean {
    return this.usedLocations.has(this.locationKey(location));
  }

  public markLocationUsed(location: PlacedLocation): void {
    this.usedLocations.add(this.locationKey(location));
  }

  /** Record a once-per-player consumption for a single player. Co-op: each
   *  player consumes independently, so this never touches the global
   *  `usedLocations` — whose presence gates the location for EVERYONE via the
   *  interactable check (a host-local use would otherwise block remote players). */
  public markUsedByPlayer(location: PlacedLocation, playerId: string): void {
    const key = this.locationKey(location);
    let set = this.usedByPlayer.get(key);
    if (!set) {
      set = new Set();
      this.usedByPlayer.set(key, set);
    }
    set.add(playerId);
  }

  /** Whether `location` is already consumed for `playerId`: per-player for
   *  once-per-player locations, global (`usedLocations`) otherwise. */
  private isConsumedFor(location: PlacedLocation, playerId: string): boolean {
    const key = this.locationKey(location);
    if (LOCATION_REGISTRY[location.id]?.oncePerPlayer) {
      return this.usedByPlayer.get(key)?.has(playerId) ?? false;
    }
    return this.usedLocations.has(key);
  }

  public triggerLocationBreak(location: PlacedLocation): void {
    this.locationsBreaking.add(this.locationKey(location));
    this.dungeonService.markNeedsRender();
  }

  public clearLocationBreaking(location: PlacedLocation): void {
    this.locationsBreaking.delete(this.locationKey(location));
    this.locationsDestroyed.add(this.locationKey(location));
    this.dungeonService.markNeedsRender();
  }

  public isLocationBreaking(location: PlacedLocation): boolean {
    return this.locationsBreaking.has(this.locationKey(location));
  }

  public isLocationDestroyed(location: PlacedLocation): boolean {
    return this.locationsDestroyed.has(this.locationKey(location));
  }

  private getFacingTile(player: PlayerEntity): { x: number; y: number } {
    const d = player.direction;
    let dx = 0;
    let dy = 0;

    if (d === 0 || d === 1 || d === 7) {
      dy = 1;
    }
    if (d === 3 || d === 4 || d === 5) {
      dy = -1;
    }
    if (d === 1 || d === 2 || d === 3) {
      dx = 1;
    }
    if (d === 5 || d === 6 || d === 7) {
      dx = -1;
    }
    return { x: player.positionX + dx, y: player.positionY + dy };
  }

  public isLocationBlockingAt(x: number, y: number): boolean {
    for (const loc of this.placedLocations) {
      if (loc.interactionType !== 'interact') {
        continue;
      }
      if (this.coversTile(loc, x, y)) {
        return true;
      }
    }
    return false;
  }

  public isLocationAt(x: number, y: number): boolean {
    for (const loc of this.placedLocations) {
      if (this.locationsDestroyed.has(this.locationKey(loc))) {
        continue;
      }
      if (this.coversTile(loc, x, y)) {
        return true;
      }
    }
    return false;
  }

  public getInteractableLocation(
    player: PlayerEntity | undefined = this.dungeonService.getPlayer(),
  ): PlacedLocation | null {
    if (!player || this.placedLocations.length === 0) {
      return null;
    }
    const facing = this.getFacingTile(player);

    for (const loc of this.placedLocations) {
      if (loc.id.startsWith('revive:')) {
        continue;
      }
      if (loc.interactionType !== 'interact') {
        continue;
      }
      if (this.isConsumedFor(loc, player.id)) {
        continue;
      }
      if (this.coversTile(loc, facing.x, facing.y)) {
        return loc;
      }
    }
    return null;
  }

  public interactWithLocation(
    location: PlacedLocation,
    triggerer?: PlayerEntity,
  ): void {
    const player = triggerer ?? this.dungeonService.getPlayer();
    if (!player) {
      return;
    }
    const locDef = LOCATION_REGISTRY[location.id];
    const key = this.locationKey(location);
    const oncePerPlayer = locDef?.oncePerPlayer ?? false;
    const isLocalPlayer = player.id === this.dungeonService.localPlayerId;

    if (this.isConsumedFor(location, player.id)) {
      return;
    }

    // requiresKey reads the LOCAL inventory. Only valid for the host's own
    // player; a remote client checks/consumes its own key before sending the
    // intent (the host can't see a client's inventory).
    if (locDef?.requiresKey && isLocalPlayer) {
      const inv = useInventoryStore.getState();
      const hasKey = inv.slots.some(
        (s) => s?.itemId === 'key' && s.quantity > 0,
      );
      if (!hasKey) {
        return;
      }
      inv.removeItem('key', 1);
    }

    const effects = locDef?.effects ?? [];
    const hasBreak = effects.some(
      (e) => e.handler === 'triggerPoiBreakAnimation',
    );

    this.runLocationWorldEffects(location, player);

    if (oncePerPlayer) {
      this.markUsedByPlayer(location, player.id);
      if (isLocalPlayer) {
        this.runLocationOwnerEffects(location, player);
        if (hasBreak) {
          this.triggerLocationBreak(location);
        }
      } else if (netService.isHost()) {
        netService.session?.broadcast(
          'reliable',
          makeLocationFire(location.id, location.x, location.y, player.id),
        );
      }
    } else {
      this.usedLocations.add(key);
      if (hasBreak) {
        this.triggerLocationBreak(location);
      }
      const hostPlayer = this.dungeonService.getPlayer();
      if (hostPlayer) {
        this.runLocationOwnerEffects(location, hostPlayer);
      }
      if (netService.isHost()) {
        netService.session?.broadcast(
          'reliable',
          makeLocationFire(location.id, location.x, location.y),
        );
      }
    }
    this.dungeonService.markNeedsRender();
  }

  private runLocationWorldEffects(
    location: PlacedLocation,
    player: PlayerEntity,
  ): void {
    const locDef = LOCATION_REGISTRY[location.id];
    const ctx: LocationEffectContext = {
      caster: player,
      targetX: location.x,
      targetY: location.y,
      direction: player.direction,
      affectedCells: [{ x: location.x, y: location.y }],
      location,
    };
    for (const eff of locDef?.effects ?? []) {
      if (
        isOwnerLocalLocationHandler(eff.handler) ||
        isConsumptionLocationHandler(eff.handler)
      ) {
        continue;
      }
      EFFECT_HANDLERS[eff.handler]?.(ctx, { ...(eff.params ?? {}) });
    }
  }

  public runLocationOwnerEffects(
    location: PlacedLocation,
    player: PlayerEntity,
  ): void {
    const locDef = LOCATION_REGISTRY[location.id];
    const ctx: LocationEffectContext = {
      caster: player,
      targetX: location.x,
      targetY: location.y,
      direction: player.direction,
      affectedCells: [{ x: location.x, y: location.y }],
      location,
    };
    for (const eff of locDef?.effects ?? []) {
      if (!isOwnerLocalLocationHandler(eff.handler)) {
        continue;
      }
      EFFECT_HANDLERS[eff.handler]?.(ctx, { ...(eff.params ?? {}) });
    }
  }

  public checkTrapAtPosition(x: number, y: number): boolean {
    if (this.placedLocations.length === 0) {
      return false;
    }
    for (const loc of this.placedLocations) {
      if (loc.id.startsWith('revive:')) {
        continue;
      }
      if (loc.interactionType !== 'walk') {
        continue;
      }
      if (this.usedLocations.has(this.locationKey(loc))) {
        continue;
      }
      if (this.coversTile(loc, x, y)) {
        const locDef = LOCATION_REGISTRY[loc.id];
        const steppers = this.dungeonService
          .getPlayers()
          .filter((p) => p.positionX === x && p.positionY === y);
        if (locDef?.isTrap) {
          const hasTrapSkip = steppers.some((p) =>
            p
              .getActiveBuffs()
              .some(
                (b) =>
                  b.def.id === 'trap-detect' || b.def.id === 'trap-traverse',
              ),
          );
          if (hasTrapSkip) {
            return false;
          }
          if (TalentEffects.hasPasDeLoup() && Math.random() < 0.5) {
            return false;
          }
        }
        this.interactWithLocation(loc, steppers[0]);
        return true;
      }
    }
    return false;
  }

  public clear(): void {
    this.placedLocations = [];
    this.usedLocations.clear();
    this.locationsBreaking.clear();
    this.locationsDestroyed.clear();
    this.usedByPlayer.clear();
  }
}
